import { KitPattern } from 'shared/kit/kit-pattern';

/** Прямоугольник в координатах формы (viewBox 100×135). */
export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Примитив SVG: тег, атрибуты в React-нотации (`stopColor`, а не `stop-color`) и дети.
 *
 * Единственный источник геометрии узоров: React-компоненты (`shared/components/kit`) и
 * генератор SVG-строки для канвы — два тонких адаптера над этим описанием. Пока узор
 * описан JSX-ом, канва и карточка команды расходятся при первой же правке.
 */
export interface ShapeNode {
  tag: string;
  attrs: Record<string, string | number>;
  children?: ShapeNode[];
}

const rect = (attrs: Record<string, string | number>): ShapeNode => ({ tag: 'rect', attrs });

const gradientNodes = (
  gradientId: string,
  area: Record<string, string | number>,
  color2: string,
  endOpacity?: number,
): ShapeNode[] => [
  {
    tag: 'defs',
    attrs: {},
    children: [
      {
        tag: 'linearGradient',
        attrs: { id: gradientId, x1: '0%', y1: '0%', x2: '100%', y2: '0%' },
        children: [
          { tag: 'stop', attrs: { offset: '0%', stopColor: 'transparent' } },
          {
            tag: 'stop',
            attrs:
              endOpacity === undefined
                ? { offset: '100%', stopColor: color2 }
                : { offset: '100%', stopColor: color2, stopOpacity: endOpacity },
          },
        ],
      },
    ],
  },
  rect({ ...area, fill: `url(#${gradientId})` }),
];

/**
 * Узор в произвольном боксе — геометрия большой формы (превью карточки команды и табло).
 *
 * `gradientId` обязан быть уникальным на документ: два комплекта на одной доске с общим id
 * склеятся по градиенту.
 */
export function garmentPatternShapes(pattern: KitPattern, box: Box, color2: string, gradientId: string): ShapeNode[] {
  const { x, y, w, h } = box;

  switch (pattern) {
    case KitPattern.STRIPES_VERTICAL: {
      const n = 7;
      const sw = w / n;
      return Array.from({ length: n })
        .map((_, i) => (i % 2 === 1 ? rect({ x: x + i * sw, y, width: sw, height: h, fill: color2 }) : null))
        .filter((node): node is ShapeNode => node !== null);
    }
    case KitPattern.STRIPES_HORIZONTAL: {
      const n = 7;
      const sh = h / n;
      return Array.from({ length: n })
        .map((_, i) => (i % 2 === 1 ? rect({ x, y: y + i * sh, width: w, height: sh, fill: color2 }) : null))
        .filter((node): node is ShapeNode => node !== null);
    }
    case KitPattern.HALVES:
      return [rect({ x: x + w / 2, y, width: w / 2, height: h, fill: color2 })];
    case KitPattern.QUARTERS:
      return [
        rect({ x, y, width: w / 2, height: h / 2, fill: color2 }),
        rect({ x: x + w / 2, y: y + h / 2, width: w / 2, height: h / 2, fill: color2 }),
      ];
    case KitPattern.SASH:
      return [
        {
          tag: 'polygon',
          attrs: {
            points: `${x + w * 0.18},${y} ${x + w * 0.5},${y} ${x + w * 0.82},${y + h} ${x + w * 0.5},${y + h}`,
            fill: color2,
          },
        },
      ];
    case KitPattern.CHECKERED: {
      const cols = 4;
      const rows = 5;
      const cw = w / cols;
      const ch = h / rows;
      return Array.from({ length: rows })
        .flatMap((_, r) =>
          Array.from({ length: cols }).map((__, c) =>
            (r + c) % 2 === 1 ? rect({ x: x + c * cw, y: y + r * ch, width: cw, height: ch, fill: color2 }) : null,
          ),
        )
        .filter((node): node is ShapeNode => node !== null);
    }
    case KitPattern.GRADIENT:
      return gradientNodes(gradientId, { x, y, width: w, height: h }, color2, 0.85);
    case KitPattern.PINSTRIPES: {
      const n = 9;
      const gap = w / n;
      return Array.from({ length: n }).map((_, i) =>
        rect({ x: x + i * gap + gap / 2, y, width: 1.4, height: h, fill: color2, opacity: 0.55 }),
      );
    }
    default:
      return [];
  }
}

/**
 * Тот же узор для свотча 14×14 — намеренно другая геометрия: в квадрате размером с иконку
 * семь полос сливаются в заливку, поэтому здесь их одна-две. Сводить обе функции в одну нельзя.
 */
export function patternOverlayShapes(
  pattern: KitPattern,
  size: { width: number; height: number },
  color2: string,
  gradientId: string,
): ShapeNode[] {
  const { width, height } = size;

  switch (pattern) {
    case KitPattern.STRIPES_VERTICAL:
      return [rect({ x: width * 0.33, y: 0, width: width * 0.34, height, fill: color2 })];
    case KitPattern.STRIPES_HORIZONTAL:
      return [rect({ x: 0, y: height * 0.33, width, height: height * 0.34, fill: color2 })];
    case KitPattern.HALVES:
      return [rect({ x: width / 2, y: 0, width: width / 2, height, fill: color2 })];
    case KitPattern.QUARTERS:
      return [
        rect({ x: 0, y: 0, width: width / 2, height: height / 2, fill: color2 }),
        rect({ x: width / 2, y: height / 2, width: width / 2, height: height / 2, fill: color2 }),
      ];
    case KitPattern.SASH:
      return [
        {
          tag: 'polygon',
          attrs: { points: `${width * 0.3},0 ${width},0 ${width * 0.7},${height} 0,${height}`, fill: color2 },
        },
      ];
    case KitPattern.CHECKERED: {
      const cw = width / 3;
      const ch = height / 3;
      return [0, 1, 2]
        .flatMap((row) =>
          [0, 1, 2].map((col) =>
            (row + col) % 2 === 1 ? rect({ x: col * cw, y: row * ch, width: cw, height: ch, fill: color2 }) : null,
          ),
        )
        .filter((node): node is ShapeNode => node !== null);
    }
    case KitPattern.GRADIENT:
      return gradientNodes(gradientId, { x: 0, y: 0, width, height }, color2);
    case KitPattern.PINSTRIPES:
      return [1, 3, 5].map((i) => rect({ x: (i / 6) * width - 0.5, y: 0, width: 1, height, fill: color2, opacity: 0.7 }));
    default:
      return [];
  }
}
